import {
  Autocomplete,
  Box,
  BoxProps,
  Chip,
  PaperProps,
  TextField,
} from "@mui/material";
import { KeyboardHints } from "~/components/KeyboardHints/KeyboardHints";
import { FilterMenuOption } from "../FilterMenu";

export type FilterBarProps = BoxProps & {
  fieldOptions: FilterMenuOption[];
  focusedField?: FilterMenuOption;
  inputValue: string;
  onInputValueChange: (value: string) => void;
  onFocusedRemove: () => void;
  onFilterSelect: (option: FilterMenuOption) => void;
  onFilterCreate: (option: string) => void;
};

export function FilterBar({
  sx,
  inputValue,
  onInputValueChange,
  fieldOptions,
  focusedField,
  onFocusedRemove,
  onFilterSelect,
  onFilterCreate,
  style,
  ...boxProps
}: FilterBarProps) {
  const groupBy = fieldOptions.some((o) => !!o.group)
    ? (option: FilterMenuOption) => option.group || "Other"
    : undefined;

  const isInputEntered = inputValue.length > 0;
  const isFieldFocused = Boolean(focusedField);
  const disableCloseOnSelect = !isInputEntered && !isFieldFocused;

  return (
    <Box sx={[...(Array.isArray(sx) ? sx : [sx])]} {...boxProps}>
      <Box
        component="form"
        noValidate
        sx={{ width: "100%" }}
        onSubmit={(e) => e.preventDefault()}
      >
        <Autocomplete
          sx={{
            backgroundColor: "code.background",
            ".MuiAutocomplete-endAdornment": { opacity: 0 },
            ".MuiOutlinedInput-notchedOutline": {
              borderWidth: "1px !important",
            },
          }}
          freeSolo
          disableCloseOnSelect={disableCloseOnSelect}
          options={fieldOptions}
          filterOptions={(options, state) => options}
          groupBy={groupBy}
          getOptionDisabled={(option) => option.isDisabled === true}
          onChange={(e, value, reason, details) => {
            onInputValueChange("");
            if (value) {
              if (typeof value === "string" && reason === "createOption") {
                onFilterCreate(value);
              }
              if (typeof value !== "string" && reason === "selectOption") {
                onFilterSelect(value);
              }
            }
          }}
          value={null}
          inputValue={inputValue}
          onInputChange={(e, value, reason) => {
            onInputValueChange(value);
          }}
          onKeyDown={(e) => {
            if (e.key === "Backspace" && inputValue === "" && focusedField) {
              onFocusedRemove();
              onInputValueChange("");
            }
          }}
          renderInput={(params) => {
            return (
              <TextField
                {...params}
                placeholder="Start typing your filter criteria..."
                slotProps={{
                  input: {
                    ...params.InputProps,
                    "aria-label": "Filter assets",
                    startAdornment: focusedField ? (
                      <Chip
                        onClick={() => onFocusedRemove()}
                        label={focusedField.label}
                        deleteIcon={<></>}
                        variant="outlined"
                        sx={{
                          borderTopLeftRadius: 4,
                          borderBottomLeftRadius: 4,
                          borderTopRightRadius: 0,
                          borderBottomRightRadius: 0,
                          backgroundColor: "background.paper",
                          borderColor: "background.light",
                        }}
                      />
                    ) : undefined,
                  },
                }}
              />
            );
          }}
          slotProps={{
            popper: {
              sx: {
                maxWidth: "400px",
              },
              placement: "bottom-start",
              modifiers: [
                {
                  name: "offset",
                  options: {
                    offset: [24, 0],
                  },
                },
                {
                  name: "flip",
                  enabled: false,
                },
              ],
            },
            listbox: {
              sx: {
                p: groupBy ? 0 : 2,
                pb: 2,

                ".MuiListSubheader-root": {
                  backgroundColor: "background.default",
                  pt: 2,
                  pb: 1,
                  fontSize: "12px",
                  fontWeight: 700,
                  lineHeight: "16px",
                },

                ".MuiAutocomplete-groupUl": {
                  px: 2,
                },
                ".MuiAutocomplete-groupUl .MuiAutocomplete-option, .MuiAutocomplete-option":
                  {
                    px: 1,
                    borderRadius: "4px",
                  },
              },
            },
            paper: {
              component: FilterBarPaper,
              sx: {
                backgroundColor: "background.default",
                p: 0,
                border: "1px solid",
                borderColor: "primary.main",
                borderTop: "none",
                borderTopLeftRadius: 0,
              },
            },
          }}
        />
      </Box>
    </Box>
  );
}

function FilterBarPaper({ children, ...paperProps }: PaperProps) {
  return (
    <Box {...paperProps}>
      <Box>{children}</Box>
      <KeyboardHints />
    </Box>
  );
}
